// /client/src/pages/Admin/DashboardAdherents/AdherentStatusBreakdownCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getSettings } from "../../../services/settingsServices";
import { getAllAdherentsListWithGroup } from "../../../services/adherentServices";

const STATUS_META = [
  {
    key: "Licencié",
    label: "Licenciés",
    bg: "bg-emerald-500",
    ring: "ring-emerald-300/40",
  },
  {
    key: "Licencié Hors Club",
    label: "Licenciés Hors Club",
    bg: "bg-emerald-300",
    ring: "ring-emerald-100/40",
  },
  {
    key: "Inscrit",
    label: "Inscrits",
    bg: "bg-amber-500",
    ring: "ring-amber-300/40",
  },
  {
    key: "Cours d'essai",
    label: "Cours d’essai",
    bg: "bg-rose-500",
    ring: "ring-rose-300/40",
  },
  {
    key: "Licencié - Arrêt",
    label: "Licenciés - Arrêt",
    bg: "bg-gray-500",
    ring: "ring-gray-300/40",
  },
];

function Row({ color, ring, label, value, percent }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`h-3 w-3 rounded-full ${color} ring-2 ${ring}`} />
        <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-slate-900 dark:text-white">
          {value.toLocaleString("fr-FR")}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {isNaN(percent) ? "—" : `${percent.toFixed(0)}%`}
        </span>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
      <div className="h-4 w-48 bg-slate-200 dark:bg-slate-800 animate-pulse rounded mb-4" />
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-28 bg-slate-200 dark:bg-slate-800 rounded" />
          </div>
          <div className="h-3 w-16 bg-slate-200 dark:bg-slate-800 rounded" />
        </div>
      ))}
      <div className="h-2 w-full bg-slate-200 dark:bg-slate-800 rounded mt-3" />
      <div className="mt-3 h-4 w-40 bg-slate-200 dark:bg-slate-800 rounded" />
    </div>
  );
}

export default function AdherentStatusBreakdownCard() {
  const [season, setSeason] = useState("");
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const settings = await getSettings();
        const activeSeason = settings?.[0]?.saison_active || "";
        if (!alive) return;
        setSeason(activeSeason);

        const list = await getAllAdherentsListWithGroup();
        if (!alive) return;

        const filtered = (Array.isArray(list) ? list : []).filter(
          (a) => String(a?.saison_active || "") === String(activeSeason)
        );

        const next = {
          Licencié: 0,
          "Licencié Hors Club": 0,
          Inscrit: 0,
          "Cours d'essai": 0,
          "Licencié - Arrêt": 0,
        };

        for (const a of filtered) {
          const s = a?.statut_inscription;
          if (s in next) next[s]++;
        }

        setCounts(next);
      } catch (e) {
        console.error("Erreur chargement détail adhérents :", e);
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const total = useMemo(
    () =>
      (counts["Licencié"] || 0) +
      (counts["Licencié Hors Club"] || 0) +
      (counts["Inscrit"] || 0) +
      (counts["Cours d'essai"] || 0) +
      (counts["Licencié - Arrêt"] || 0),
    [counts]
  );

  if (loading) return <Skeleton />;

  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5 shadow-sm">
      {/* Titre */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
            Détail adhérents
          </h4>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            Saison {season || "—"}
          </div>
        </div>
      </div>

      {/* Lignes */}
      <div className="space-y-2">
        {STATUS_META.map(({ key, label, bg, ring }) => {
          const val = counts[key] || 0;
          const pct = total > 0 ? (val / total) * 100 : NaN;
          return (
            <Row
              key={key}
              color={bg}
              ring={ring}
              label={label}
              value={val}
              percent={pct}
            />
          );
        })}
      </div>

      {/* Barre cumulée */}
      <div className="mt-3 h-2 w-full rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-100 dark:bg-slate-800">
        <div className="flex h-full w-full">
          {STATUS_META.map(({ key, bg }) => {
            const val = counts[key] || 0;
            const width = total > 0 ? `${(val / total) * 100}%` : "0%";
            return (
              <div
                key={key}
                className={`${bg}`}
                style={{ width }}
                title={`${key}: ${val.toLocaleString("fr-FR")}`}
              />
            );
          })}
        </div>
      </div>

      {/* Total */}
      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Total saison
        </div>
        <div className="text-lg font-bold text-slate-900 dark:text-white">
          {total.toLocaleString("fr-FR")}
        </div>
      </div>
    </div>
  );
}
